import { WorkerFormService } from 'src/app/data/worker-form.service';
import { Component, Input, OnChanges, OnInit, inject } from '@angular/core';
import { filter, finalize, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { MatStepper } from '@angular/material/stepper';
import { Observable, of, Subject } from 'rxjs';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { QuestionnaireProcessStep } from 'src/app/common/enums';
import { WorkerService } from 'src/app/data/worker.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SetSkipEducationHistoryRequest } from 'src/app/contracts/requests/worker-form/set-skip-education-history-request';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-education-history-step',
  templateUrl: './education-history-step.component.html',
  styleUrls: ['./education-history-step.component.scss'],
  standalone: false,
})
export class EducationHistoryStepComponent implements OnInit {
  private workerFormService = inject(WorkerFormService);
  private snackbar = inject(SnackBarService);
  private workerService = inject(WorkerService);
  private spinner = inject(NgxSpinnerService);

  private readonly unsubscribe$ = new Subject<void>();

  @Input() workerId: number;
  @Input() workerFormId: number;
  @Input() stepper: MatStepper;
  @Input() isLoggedUserProfile: boolean;
  @Input() isEducationHistoryAlertVisible: boolean;

  selectedStepIndex: number;
  isStudent: boolean;
  isNextButtonDisabled: boolean = false;
  setSkipEducationHistoryChxValue: boolean = false;
  workerFormApproved: boolean = false;

  constructor() { }

  ngOnInit(): void {
    this.stepper.selectionChange.pipe(takeUntil(this.unsubscribe$)).subscribe((sc: StepperSelectionEvent) => {
      if (
        sc.selectedIndex > sc.previouslySelectedIndex &&
        sc.previouslySelectedIndex === QuestionnaireProcessStep.EducationHistory - 1 &&
        this.isNextButtonDisabled
      ) {
        setTimeout(() => {
          this.stepper.selectedIndex = sc.previouslySelectedIndex;
        });
        return;
      }

      if ((sc.selectedStep.label as any as QuestionnaireProcessStep) !== QuestionnaireProcessStep.EducationHistory) {
        return;
      }

      this.workerService.getEducationHistorySkipInformation(this.workerId).subscribe({
        next: (res) => {
          if (res.WorkerFormApproved) {
            this.isNextButtonDisabled = false;
            this.workerFormApproved = res.WorkerFormApproved;
          } else {
            this.setSkipEducationHistoryChxValue = res.SkipEducationHistory;
            this.isNextButtonDisabled = !this.setSkipEducationHistoryChxValue && !res.AnyEducationHistoryEntry;
          }
        },
        error: (error: HttpErrorResponse) => {
          this.snackbar.openErrorSnackBar(error.error);
        },
      });
    });
  }

  private checkStudentStatus(): Observable<void> {
    if (!this.workerFormId) {
      return of(undefined);
    }

    return this.workerFormService.getWorkerStatementByCode(this.workerFormId, 'Student').pipe(
      first(),
      map((statement) => {
        if (statement?.BooleanAnswer) {
          this.snackbar.openSuccessSnackBar(Messages.StudentAlert);
        }
      }),
    );
  }

  submitAndContinue(): void {
    if (this.isNextButtonDisabled) {
      return;
    }

    if (this.workerFormApproved) {
      this.checkStudentStatus().subscribe(() => this.stepper.next());
      return;
    }

    this.spinner.show();

    const req: SetSkipEducationHistoryRequest = {
      SkipEducationHistory: this.setSkipEducationHistoryChxValue,
    };

    this.workerFormService
      .setSkipEducationHistory(this.workerFormId, req)
      .pipe(
        switchMap(() => this.checkStudentStatus()),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => this.stepper.next(),
        error: (error: HttpErrorResponse) => this.snackbar.openErrorSnackBar(error.error),
      });
  }

  onEducationHistoryChanged(event: any) {
    this.setSkipEducationHistoryChxValue = event.chxValue;
    this.isNextButtonDisabled = !event.chxValue && !event.anyEducationHistoryEntry;
  }
}
